var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Log } from '../../../../log';
import { TelnetNegotiator } from './TelnetNegotiator';
const connectTimeoutMs = 8000;
class Subject {
    constructor() {
        this.observers = {};
    }
    next(value) {
        for (const observer of Object.values(this.observers)) {
            observer(value);
        }
    }
    subscribe(callback) {
        const id = Math.random().toString();
        this.observers[id] = callback;
        return { unsubscribe: () => delete this.observers[id] };
    }
}
export class Socket {
    constructor(logFactory = Log) {
        this._state = 'disconnected';
        this.onCloseSubject = new Subject();
        this.sendPromiseId = 0;
        this.sendPromises = {};
        this.socket = new AveoTcpSocket();
        this.socket.onData = this.socketDataCallback.bind(this);
        this.socket.onError = this.socketErrorCallback.bind(this);
        this.socket.onClose = (hasError) => {
            this.onCloseSubject.next(hasError);
        };
        this.driverLog = logFactory.channel('Socket', 'driver');
        this.log = logFactory.channel('Socket');
        this.driverLogDebug = this.driverLogDebug.bind(this);
        this.send = this.send.bind(this);
        const onCloseSubscription = this.onCloseSubject.subscribe(hasError => {
            var _a;
            if (this.closeReason === undefined) {
                if (hasError) {
                    this.log.info(this.format('unexpectedly closed due to error'));
                }
                else {
                    this.log.info(this.format('closed'));
                }
                if (this._state === 'disconnected') {
                    this.log.warning(this.format('already disconnected'));
                    return;
                }
                (_a = this.clientConnectionLostCallback) === null || _a === void 0 ? void 0 : _a.call(this);
            }
            onCloseSubscription.unsubscribe();
        });
    }
    // getters and setters
    get host() {
        return this._host;
    }
    get key() {
        return this.socket.key;
    }
    get negotiateTelnet() {
        return this.telnetNegotiator !== undefined;
    }
    set negotiateTelnet(value) {
        if (value && this.telnetNegotiator === undefined) {
            this.telnetNegotiator = new TelnetNegotiator();
        }
        else if (!value && this.telnetNegotiator !== undefined) {
            this.telnetNegotiator = undefined;
        }
    }
    get port() {
        return this._port;
    }
    get state() {
        return this._state;
    }
    close(closeReason) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.closeReason !== undefined) {
                if (this._state === 'disconnected') {
                    throw new Error(this.format(`already closed due to ${this.closeReason}`));
                }
                else {
                    throw new Error(this.format(`already closing due to ${this.closeReason}`));
                }
            }
            this._state = 'disconnecting';
            // Reject any pending sends.
            const disconnectedError = new Error(this.format('disconnected'));
            for (const sendPromise of Object.values(this.sendPromises)) {
                sendPromise.reject(disconnectedError);
            }
            this.sendPromises = {};
            this.closeReason = closeReason;
            return new Promise((resolve, reject) => {
                // The success callback from the close method does not mean that the socket is closed,
                // only that the request has been received. The socketCloseCallback method (assigned to
                // the socket's onClose property) is called when the socket is actually closed.
                const onCloseSubscription = this.onCloseSubject.subscribe(hasError => {
                    if (this.closeReason !== closeReason) {
                        this.log.error(this.format(`close: unexpected close reason: "${this.closeReason}", expected "${closeReason}"`));
                    }
                    if (hasError) {
                        this.log.error(this.format(`close: unexpected error`));
                    }
                    this.log.info(this.format(`closed due to ${closeReason}`));
                    this._state = 'disconnected';
                    onCloseSubscription.unsubscribe();
                    resolve();
                });
                this.socket.close(() => {
                    this.log.debug(this.format(`close started for ${closeReason}`));
                }, (message) => {
                    this._state = 'disconnected';
                    reject(new Error(this.format(`error closing socket: ${message}`)));
                    onCloseSubscription.unsubscribe();
                });
            });
        });
    }
    connect(host, port, username, // specify username and password for ssh
    password) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.closeReason !== undefined) {
                return Promise.reject(new Error(this.format(`can't connect after being disconnected`)));
            }
            if (this._state !== 'disconnected') {
                return Promise.reject(new Error(this.format('not disconnected')));
            }
            this._host = host;
            this._port = port;
            this._state = 'connecting';
            let promiseHandled = false;
            this.log.info(this.format('connecting'));
            if (this.telnetNegotiator)
                this.telnetNegotiator.reset();
            return new Promise((resolve, reject) => {
                // Set a connect timeout that is longer than the timeout used in the plugin. This is a
                // backup in case the plugin fails to timeout.
                const connectTimeout = window.setTimeout(() => __awaiter(this, void 0, void 0, function* () {
                    this.log.info(this.format('connect timeout'));
                    try {
                        yield this.close('connect timeout');
                    }
                    catch (error) {
                        this.log.error(this.format(error.message));
                        this.log.info(this.format('closed due to connect timeout'));
                    }
                    if (!promiseHandled) {
                        promiseHandled = true;
                        reject(new Error(this.format('connect timeout')));
                    }
                }), connectTimeoutMs + 2000);
                this.socket.open(username === undefined ? 'plain' : 'ssh', host, port, username, password, connectTimeoutMs, () => {
                    window.clearTimeout(connectTimeout);
                    if (this._state === 'connecting') {
                        this.log.info(this.format('connected'));
                        this._state = 'connected';
                        if (!promiseHandled) {
                            promiseHandled = true;
                            resolve();
                        }
                    }
                    else {
                        if (!promiseHandled) {
                            promiseHandled = true;
                            reject(new Error(this.format(`open: found ${this._state} instead of connecting`)));
                        }
                    }
                }, (message) => {
                    window.clearTimeout(connectTimeout);
                    this._state = 'disconnected';
                    if (!promiseHandled) {
                        promiseHandled = true;
                        reject(new Error(this.format(`error opening socket: ${message}`)));
                    }
                });
            });
        });
    }
    disconnect() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (this._state !== 'connected') {
                throw new Error(`can't disconnect while ${this._state}`);
            }
            try {
                yield this.close('disconnect');
            }
            catch (error) {
                this.log.error(this.format(error.message));
            }
            (_a = this.clientConnectionLostCallback) === null || _a === void 0 ? void 0 : _a.call(this);
        });
    }
    driverLogDebug(message) {
        this.driverLog.debug(this.format(message));
    }
    format(message) {
        return `[${this.socket.key}] ${message}`;
    }
    onConnectionLost(connectionLostCallback) {
        this.clientConnectionLostCallback = connectionLostCallback;
        return this;
    }
    onData(dataCallback) {
        this.clientDataCallback = dataCallback;
        return this;
    }
    send(data) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._state !== 'connected') {
                return Promise.reject(new Error(this.format(`can't send while ${this._state}`)));
            }
            return new Promise((resolve, reject) => {
                const id = (this.sendPromiseId++).toString();
                this.sendPromises[id] = { resolve, reject };
                this.socket.write(data, () => {
                    delete this.sendPromises[id];
                    resolve();
                }, (message) => {
                    delete this.sendPromises[id];
                    reject(new Error(this.format(message)));
                });
            });
        });
    }
    socketDataCallback(data) {
        if (this.telnetNegotiator) {
            data = this.telnetNegotiator.process(data, this.send, this.driverLogDebug);
        }
        if (this.clientDataCallback) {
            this.clientDataCallback(data);
        }
    }
    socketErrorCallback(message) {
        this.log.warning(this.format(`disconnecting due to error: ${message}`));
        if (this.deferredClosePromise) {
            this.deferredClosePromise.reject(new Error(this.format(message)));
            this.deferredClosePromise = undefined;
        }
        if (this._state === 'connected' && this.clientConnectionLostCallback) {
            this.clientConnectionLostCallback();
        }
        this._state = 'disconnected';
    }
}
